import { Component, inject, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { AssetOverviewService } from '../../../../services/asset-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { KmOption } from '../../../../models/km-option.model';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-edit-lifecycle-points-modal',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './edit-lifecycle-points-modal.component.html',
  styleUrl: './edit-lifecycle-points-modal.component.scss'
})
export class EditLifecyclePointsModalComponent implements OnDestroy {
  @Input() assetId: number = 0;
  @Input() assetName: string | null = null;
  @Input() licensePlate: string | null = null;
  @Input() mainColour: string | null = null;
  @Input() mainColourHex: string | null = null;
  @Input() sellingPointKm: number | null = null;
  @Input() retirePointKm: number | null = null;

  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private assetOverviewService: AssetOverviewService = inject(AssetOverviewService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private toastr: ToastrService = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  private readonly KM_OPTION_STEP = 100_000;
  private readonly KM_OPTION_MAX = 2_000_000;

  readonly kmOptions: KmOption[] = this.buildKmOptions();

  isSaving: boolean = false;

  private cancellation = new Subject<void>();

  // Captured on the first edit rather than in ngOnInit: the parent assigns the @Inputs after
  // NgbModal creates the component, so there is nothing to snapshot yet when ngOnInit runs.
  private initial: { assetName: string; licensePlate: string; sellingPointKm: number | null; retirePointKm: number | null } | null = null;

  private captureInitialValues(): void {
    this.initial ??= {
      assetName: this.assetName ?? '',
      licensePlate: this.licensePlate ?? '',
      sellingPointKm: this.sellingPointKm,
      retirePointKm: this.retirePointKm
    };
  }

  get validationMessage(): string | null {
    if (this.assetName != null && this.assetName.trim() === '') {
      return this.transloco.translate('assetOverview.lifecycleModal.validation.assetNameRequired');
    }

    // Lifecycle data is optional, but the backend stores the two points as a pair.
    if ((this.sellingPointKm != null) !== (this.retirePointKm != null)) {
      return this.transloco.translate('assetOverview.lifecycleModal.validation.bothPoints');
    }

    if (this.sellingPointKm != null && this.sellingPointKm <= 0) {
      return this.transloco.translate('assetOverview.lifecycleModal.validation.sellingPointPositive');
    }

    if (this.retirePointKm != null && this.retirePointKm <= 0) {
      return this.transloco.translate('assetOverview.lifecycleModal.validation.retirementPointPositive');
    }

    if (this.sellingPointKm != null && this.retirePointKm != null && this.sellingPointKm > this.retirePointKm) {
      return this.transloco.translate('assetOverview.lifecycleModal.validation.sellingPointExceedsRetirement');
    }

    return null;
  }

  get hasChanges(): boolean {
    if (this.initial === null) {
      return false;
    }

    return (this.assetName ?? '') !== this.initial.assetName
      || (this.licensePlate ?? '') !== this.initial.licensePlate
      || this.sellingPointKm !== this.initial.sellingPointKm
      || this.retirePointKm !== this.initial.retirePointKm;
  }

  get canSave(): boolean {
    return this.hasChanges
      && this.validationMessage === null
      && this.assetName != null
      && this.assetName.trim() !== '';
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  onAssetNameInput(event: Event): void {
    this.captureInitialValues();
    this.assetName = (event.target as HTMLInputElement).value;
  }

  onLicensePlateInput(event: Event): void {
    this.captureInitialValues();
    this.licensePlate = (event.target as HTMLInputElement).value;
  }

  onSellingPointInput(event: Event): void {
    this.captureInitialValues();
    this.sellingPointKm = this.parseKm(event);
  }

  onRetirePointInput(event: Event): void {
    this.captureInitialValues();
    this.retirePointKm = this.parseKm(event);
  }

  selectSellingPoint(value: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.captureInitialValues();
    this.sellingPointKm = value;
  }

  selectRetirePoint(value: number, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.captureInitialValues();
    this.retirePointKm = value;
  }

  async onSave(): Promise<void> {
    if (!this.canSave || this.isSaving) return;

    this.isSaving = true;
    try {
      await this.assetOverviewService.saveAssetDetails({
        assetId: this.assetId,
        assetName: this.assetName!.trim(),
        licensePlate: this.licensePlate?.trim() || null,
        sellingPointKm: this.sellingPointKm,
        retirePointKm: this.retirePointKm
      }, this.cancellation);

      this.toastr.success(this.transloco.translate('assetOverview.lifecycleModal.toasts.saved'));
      this.activeModal.close(true);
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isSaving = false;
        }
      });
    }
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  private buildKmOptions(): KmOption[] {
    return Array.from(
      { length: this.KM_OPTION_MAX / this.KM_OPTION_STEP },
      (_, index) => {
        const value = (index + 1) * this.KM_OPTION_STEP;
        return { value, label: this.formatKm(value) };
      }
    );
  }

  private formatKm(value: number): string {
    return `${value.toLocaleString('en-US').replace(/,/g, ' ')} km`;
  }

  private parseKm(event: Event): number | null {
    const value = (event.target as HTMLInputElement).value;
    return value === '' ? null : Number(value);
  }
}
